import { ChevronDown } from 'lucide-react'
import { CurrencyDollarIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface RangeFilterProps {
  title: string
  placeholder?: string
  valueFrom: string
  valueTo: string
  onValueFromChange: (value: string) => void
  onValueToChange: (value: string) => void
  icon?: React.ReactNode
  className?: string
  currency?: boolean
}

export const RangeFilter = ({
  title,
  placeholder = 'Seleccionar rango...',
  valueFrom,
  valueTo,
  onValueFromChange,
  onValueToChange,
  icon,
  className,
  currency = false,
}: RangeFilterProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const hasActiveRange = valueFrom || valueTo
  
  const clearRange = () => {
    onValueFromChange('')
    onValueToChange('')
  }

  const formatValue = (value: string) => {
    if (!value) return ''
    if (currency) {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR'
      }).format(parseFloat(value))
    }
    return value
  }

  const getDisplayText = () => {
    if (valueFrom && valueTo) {
      return `${formatValue(valueFrom)} - ${formatValue(valueTo)}`
    } else if (valueFrom) {
      return `Desde ${formatValue(valueFrom)}`
    } else if (valueTo) {
      return `Hasta ${formatValue(valueTo)}`
    }
    return title
  }

  const getActiveCount = () => {
    let count = 0
    if (valueFrom) count++
    if (valueTo) count++
    return count
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('gap-1.5', className)}
          aria-label={title}
        >
          {icon || <CurrencyDollarIcon className="h-4 w-4" />}
          <span>{getDisplayText()}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
          {hasActiveRange && (
            <Badge variant="secondary" className="ml-1 rounded-full px-1.5">
              {getActiveCount()}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">{title}</h4>
            <p className="text-sm text-muted-foreground">
              Selecciona un rango de valores
            </p>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Desde
              </label>
              <input
                type="number"
                step="0.01"
                value={valueFrom}
                onChange={(e) => onValueFromChange(e.target.value)}
                placeholder="0.00"
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Hasta
              </label>
              <input
                type="number"
                step="0.01"
                value={valueTo}
                onChange={(e) => onValueToChange(e.target.value)}
                placeholder="0.00"
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
              />
            </div>
          </div>
          
          {hasActiveRange && (
            <div className="flex justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={clearRange}
                className="text-xs"
              >
                Limpiar
              </Button>
              <Button
                size="sm"
                onClick={() => setIsOpen(false)}
                className="text-xs"
              >
                Aplicar
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
